/**
 * <dj-subscribe-save> — "One-time purchase / Subscribe & save" options in the product form.
 *
 * Markup: snippets/dj-subscribe-save.liquid. The visible radios are not named `selling_plan`:
 * Shopify's accelerated-checkout script reads the FIRST form control with that name regardless
 * of :checked, so the form carries exactly one hidden `selling_plan` input and this element keeps
 * it in step with the radios:
 *   subscribe → value = plan id, enabled   (FormData posts it to /cart/add.js)
 *   one-time  → value = "",     disabled  (FormData skips it — same request as before the widget)
 * The server renders that input off; it only turns on here, so without this script nothing posts a
 * plan. The value is changed with setAttribute because the wallets watch that attribute to re-sync
 * (and to show Shopify's #shopify-buyer-consent subscription notice).
 *
 * Frequencies: when the plan group has several plans (every 2 weeks / month / 2 months / …), a row
 * of `dj_frequency` pills follows the Subscribe card (shown by CSS while Subscribe is checked). They
 * are deliberately not .dj-sub__radio: the Subscribe radio stays the one source of the plan id.
 * Picking a pill copies its plan id onto the Subscribe radio's value, checks Subscribe (choosing a
 * frequency means subscribing) and redraws the card's price, compare-at, "Save N%" and cadence from
 * the pill's data-* — then the hidden input, URL, size pills and sticky bar follow as for any choice.
 *
 * Keeping the choice when the variant changes:
 *   - new-pdp (every live product): size pills are ?variant= links, i.e. full page loads. The
 *     choice is mirrored into ?selling_plan= on the page URL and on the pills, and the Liquid
 *     renders the checked radio from product.selected_selling_plan.
 *   - Horizon's variant-picker (templates/product.json, quick add): on variant:update we take this
 *     widget's markup from the server HTML (fresh prices; that HTML never has a plan selected) and
 *     re-apply the shopper's choice — to the frequency pills and the Subscribe radio — falling back
 *     to one-time when the new variant doesn't offer that plan. On the page the choice is kept in
 *     `this.choice`, because a whole-product morph resets the radios before the event fires. In the
 *     quick-add dialog only the picker is morphed, but the dialog morphs each product's markup into
 *     this same element (no reconnect), so there the radios, not `this.choice`, are the truth.
 *
 * The sticky add-to-cart bar re-states the price and submits this same form, so while a plan is
 * selected it shows the subscription price too (it would otherwise advertise the one-time price
 * for what is actually a subscription add).
 *
 * Loaded globally (snippets/scripts.liquid) so copies inside the quick-add modal upgrade too.
 * Self-contained: no @theme imports, only the documented variant:update event.
 */
if (!customElements.get('dj-subscribe-save')) {
  class DjSubscribeSave extends HTMLElement {
    /** @type {string} The shopper's choice: a plan id, or '' for a one-time purchase. */
    choice = '';

    connectedCallback() {
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onFrequencyClick);
      this.scope = this.closest('.shopify-section, dialog');
      this.scope?.addEventListener('variant:update', this.onVariantUpdate);
      this.setAttribute('data-enhanced', 'true');

      // A frequency tapped before this deferred script ran hasn't reached the Subscribe radio yet.
      // Carry it over, but leave the purchase option as the shopper last set it.
      const frequency = this.frequencyRadios.find((radio) => radio.checked);
      if (frequency && frequency.value !== this.planRadio?.value) this.applyFrequency(frequency);

      // Adopt whatever is checked now: the server's ?selling_plan= state, or a tap made before this
      // deferred script ran — in that case the URL and size pills haven't heard about it yet.
      this.choice = this.selectedPlan;
      this.sync();
      if ([...this.radios, ...this.frequencyRadios].some((radio) => radio.checked !== radio.defaultChecked)) {
        this.persist();
      }
      this.syncSticky();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onFrequencyClick);
      this.scope?.removeEventListener('variant:update', this.onVariantUpdate);
      this.scope = null;
    }

    get input() {
      return this.querySelector('input[name="selling_plan"]');
    }

    get radios() {
      return Array.from(this.querySelectorAll('.dj-sub__radio'));
    }

    /** The Subscribe radio: its value is the plan id of the chosen frequency. */
    get planRadio() {
      return this.querySelector('.dj-sub__option--plan .dj-sub__radio');
    }

    /** The frequency pills' radios (only when the plan group has more than one plan). */
    get frequencyRadios() {
      return Array.from(this.querySelectorAll('.dj-sub__freq-radio'));
    }

    /** The checked plan id, or '' for a one-time purchase. */
    get selectedPlan() {
      return this.radios.find((radio) => radio.checked)?.value ?? '';
    }

    onChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      if (target.classList.contains('dj-sub__freq-radio')) {
        // Choosing a frequency means subscribing at it.
        this.applyFrequency(target);
        const plan = this.planRadio;
        if (plan) plan.checked = true;
      } else if (!target.classList.contains('dj-sub__radio')) {
        return;
      }

      this.choice = this.selectedPlan;
      this.sync();
      this.persist();
      this.syncSticky();
    };

    /**
     * A click on the frequency pill that is already checked fires no `change`. That happens where the
     * pills show under One-time (browsers without :has(), where CSS can't hide them): picking the
     * drawn-as-selected pill must still mean "subscribe at this frequency".
     */
    onFrequencyClick = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.classList.contains('dj-sub__freq-radio')) return;
      // A different pill gets a native `change` of its own; only the already-current one needs this.
      const plan = this.planRadio;
      if (!plan || plan.checked || plan.value !== target.value) return;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    };

    /**
     * Point the Subscribe option at a frequency: the radio's value becomes that plan id, and the card's
     * price, compare-at, "Save N%" and cadence are redrawn from the pill's data-* (server-formatted).
     * Doesn't check anything — callers decide whether the purchase option changes.
     * @param {HTMLInputElement} frequency
     */
    applyFrequency(frequency) {
      const plan = this.planRadio;
      const card = plan?.closest('.dj-sub__option');
      if (!plan || !card) return;

      plan.value = frequency.value;
      const { price = '', comparePrice = '', saveLabel = '', cadence = '' } = frequency.dataset;

      const now = card.querySelector('.dj-sub__price-now');
      if (now && price) now.textContent = price;

      let compare = card.querySelector('.dj-sub__compare');
      if (comparePrice) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'dj-sub__compare';
          card.querySelector('.dj-sub__price')?.append(compare);
        }
        let hint = compare.querySelector('.visually-hidden');
        if (!hint) {
          hint = document.createElement('span');
          hint.className = 'visually-hidden';
          hint.textContent = 'Regular price';
        }
        compare.replaceChildren(hint, ` ${comparePrice}`);
      } else {
        compare?.remove();
      }

      let save = card.querySelector('.dj-sub__save');
      if (saveLabel) {
        if (!save) {
          save = document.createElement('span');
          save.className = 'dj-sub__save';
          card.querySelector('.dj-sub__title')?.append(save);
        }
        save.textContent = saveLabel;
      } else {
        save?.remove();
      }

      const note = card.querySelector('.dj-sub__cadence');
      if (note && cadence) note.textContent = cadence;
    }

    /** Mirror the checked radio into the hidden selling_plan input. */
    sync() {
      const plan = this.selectedPlan;
      this.setInput(plan);
    }

    /** @param {string} plan */
    setInput(plan) {
      const input = this.input;
      if (!input) return;
      if (input.getAttribute('value') === plan && input.disabled === !plan) return;

      input.setAttribute('value', plan);
      input.toggleAttribute('disabled', !plan);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /** Keep ?selling_plan= on the page URL and the size pills so a size change keeps the choice. */
    persist() {
      // In the quick-add modal the URL and the pills belong to the page underneath — leave them.
      if (this.closest('dialog')) return;

      const plan = this.selectedPlan;
      const withPlan = (href) => {
        const url = new URL(href, window.location.href);
        if (plan) {
          url.searchParams.set('selling_plan', plan);
        } else {
          url.searchParams.delete('selling_plan');
        }
        return url;
      };

      history.replaceState(history.state, '', withPlan(window.location.href).toString());

      // dj-variant-cards renders its pills as relative "?variant=…" links; keep them relative.
      this.closest('.shopify-section')
        ?.querySelectorAll('a.dj-vc__pill[href]')
        .forEach((pill) => {
          const href = pill.getAttribute('href');
          if (href?.startsWith('?')) pill.setAttribute('href', withPlan(href).search);
        });
    }

    /** Show the subscription price in the section's sticky add-to-cart bar while a plan is selected. */
    syncSticky() {
      if (this.closest('dialog')) return;
      const slot = this.closest('.shopify-section')?.querySelector('sticky-add-to-cart .sticky-add-to-cart__price');
      if (!slot) return;

      slot.querySelector(':scope > .dj-sub__sticky')?.remove();
      const price = this.selectedPlan && !this.hidden ? this.querySelector('.dj-sub__radio:checked')
        ?.closest('.dj-sub__option')
        ?.querySelector('.dj-sub__price-now')
        ?.textContent.trim() : '';

      if (!price) {
        slot.classList.remove('dj-sub-sticky-on');
        return;
      }

      const node = document.createElement('span');
      node.className = 'dj-sub__sticky';
      const amount = document.createElement('span');
      amount.textContent = price;
      const label = document.createElement('span');
      label.className = 'dj-sub__sticky-label';
      label.textContent = 'Subscription';
      node.append(amount, label);
      slot.append(node);
      slot.classList.add('dj-sub-sticky-on');
    }

    /** Horizon variant-picker path: refresh prices from the server HTML, keep the shopper's choice. */
    onVariantUpdate = (event) => {
      const data = event.detail?.data;
      if (!data?.html) return;

      if (data.newProduct) {
        // Combined listing switched products while only the picker was morphed: follow the new
        // product (as product-form.js and product-price.js do) and take its widget markup below.
        this.dataset.productId = String(data.newProduct.id);
      } else {
        const sourceProductId = event.target instanceof HTMLElement ? event.target.dataset.productId : undefined;
        if (sourceProductId && sourceProductId !== this.dataset.productId) return;
      }

      // In the quick-add dialog the radios are current (only the picker morphs there) while
      // `this.choice` may belong to a product the dialog showed earlier in this same element.
      const keep = this.closest('dialog') ? this.selectedPlan : this.choice;
      const fresh = data.html.querySelector(
        `dj-subscribe-save[data-block-id="${CSS.escape(this.dataset.blockId ?? '')}"]`
      );

      if (!fresh) {
        // The new variant has no subscription option: hide, and make sure nothing posts a plan.
        this.hidden = true;
        const oneTime = this.radios.find((radio) => radio.value === '');
        if (oneTime) oneTime.checked = true;
        this.setInput('');
      } else {
        this.hidden = false;
        this.dataset.productId = fresh.dataset.productId ?? this.dataset.productId;
        this.replaceChildren(...Array.from(fresh.childNodes, (node) => document.importNode(node, true)));

        // The fresh markup has the default frequency on the Subscribe radio: move it to the kept plan
        // when this variant offers it, then check the matching purchase option (one-time otherwise).
        const frequency = this.frequencyRadios.find((radio) => radio.value === keep);
        if (frequency) {
          frequency.checked = true;
          this.applyFrequency(frequency);
        }
        const radios = this.radios;
        const match = radios.find((radio) => radio.value === keep) ?? radios.find((radio) => radio.value === '');
        if (match) match.checked = true;
        this.sync();
      }

      // sticky-add-to-cart morphs its own bar from the same event; re-apply once every listener has
      // run. (A timeout rather than requestAnimationFrame, which never fires in a background tab.)
      setTimeout(() => this.syncSticky(), 0);
    };
  }

  customElements.define('dj-subscribe-save', DjSubscribeSave);
}
